import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { projectService } from '@/services/projectService';
import { metricCatalogService, CheckMappingResponse, CheckMappingRow } from '@/services/metricCatalogService';

export function CheckMappingTab() {
  const [selectedProjectId, setSelectedProjectId] = useState<number | ''>('');

  const projectsQuery = useQuery({
    queryKey: ['metricCatalog', 'checkMapping', 'projects'],
    queryFn: async () => {
      const res = await projectService.getProjects({ page: 1, itemsPerPage: 200 });
      return res.data;
    },
  });

  useEffect(() => {
    if (selectedProjectId !== '') return;
    const first = projectsQuery.data?.[0];
    if (first?.id) {
      setSelectedProjectId(first.id);
    }
  }, [projectsQuery.data, selectedProjectId]);

  const checkMutation = useMutation({
    mutationFn: async (projectId: number) => metricCatalogService.checkMapping(projectId),
  });

  const rows: CheckMappingRow[] = useMemo(() => {
    const base = checkMutation.data?.rows ?? [];
    const statusRank: Record<CheckMappingRow['status'], number> = {
      ok: 0,
      no_value: 1,
      no_attribute: 2,
    };

    return [...base].sort((a, b) => {
      const aHasValue = a.currentValue !== null && a.currentValue !== undefined;
      const bHasValue = b.currentValue !== null && b.currentValue !== undefined;
      if (aHasValue !== bHasValue) return aHasValue ? -1 : 1;

      const ar = statusRank[a.status] ?? 99;
      const br = statusRank[b.status] ?? 99;
      if (ar !== br) return ar - br;

      return a.attributeType.localeCompare(b.attributeType);
    });
  }, [checkMutation.data]);

  const summary: Pick<CheckMappingResponse, 'boxesCount' | 'mappingsCount' | 'generatedAt' | 'message'> | null = useMemo(() => {
    if (!checkMutation.data) return null;
    return {
      boxesCount: checkMutation.data.boxesCount,
      mappingsCount: checkMutation.data.mappingsCount,
      generatedAt: checkMutation.data.generatedAt,
      message: checkMutation.data.message,
    };
  }, [checkMutation.data]);

  const handleRun = () => {
    if (selectedProjectId === '') return;
    checkMutation.mutate(Number(selectedProjectId));
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Check mapping</h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Vérifie (via Zipato) qu’un type d’attribut mappé correspond bien à une donnée réelle. On affiche 1 valeur courante par
            AttributeType (pas 1 par attribut).
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="text-sm text-slate-700 dark:text-slate-300">
            <span className="mr-2">Projet</span>
            <select
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value ? Number(e.target.value) : '')}
              className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-[#0B1116] text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">Sélectionner…</option>
              {(projectsQuery.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title}
                </option>
              ))}
            </select>
          </label>
          <button
            onClick={handleRun}
            disabled={selectedProjectId === '' || checkMutation.isPending}
            className="px-4 py-2 rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {checkMutation.isPending ? 'Vérification…' : 'Lancer'}
          </button>
        </div>
      </div>

      {projectsQuery.isLoading && (
        <div className="text-sm text-slate-500 dark:text-slate-400">Chargement des projets…</div>
      )}
      {projectsQuery.isError && (
        <div className="text-sm text-red-600 dark:text-red-400">Erreur lors du chargement des projets.</div>
      )}

      {checkMutation.isError && (
        <div className="mb-4 text-sm text-red-600 dark:text-red-400">Erreur pendant la vérification (Zipato/API).</div>
      )}

      {summary && (
        <div className="mb-4 text-sm text-slate-600 dark:text-slate-400">
          <div>
            Généré à: <span className="font-mono">{summary.generatedAt}</span> — Boxes: {summary.boxesCount} — Mappings:{' '}
            {summary.mappingsCount}
          </div>
          {summary.message && <div className="mt-1">{summary.message}</div>}
        </div>
      )}

      <div className="bg-white dark:bg-[#16202A] border border-slate-200 dark:border-[#1E293B] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 dark:bg-[#0B1116] text-slate-500 dark:text-slate-400">
              <tr>
                <th className="text-left font-medium px-5 py-3">AttributeType</th>
                <th className="text-left font-medium px-5 py-3">Domaine</th>
                <th className="text-left font-medium px-5 py-3">Metric</th>
                <th className="text-left font-medium px-5 py-3">Variant</th>
                <th className="text-left font-medium px-5 py-3">ValueType</th>
                <th className="text-left font-medium px-5 py-3">Unit</th>
                <th className="text-left font-medium px-5 py-3">current value</th>
                <th className="text-left font-medium px-5 py-3">current time stamp</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-5 py-8 text-center text-slate-500 dark:text-slate-400">
                    {checkMutation.isPending
                      ? 'Vérification en cours…'
                      : 'Aucune donnée. Sélectionne un projet puis clique sur “Lancer”.'}
                  </td>
                </tr>
              ) : (
                rows.map((r, idx) => (
                  <tr key={`${r.attributeType}-${idx}`} className="border-t border-slate-100 dark:border-[#1E293B]">
                    <td className="px-5 py-3 font-medium text-slate-900 dark:text-white">{r.attributeType}</td>
                    <td className="px-5 py-3">{r.domain}</td>
                    <td className="px-5 py-3">{r.metric}</td>
                    <td className="px-5 py-3">{r.variant ?? ''}</td>
                    <td className="px-5 py-3">{r.valueType}</td>
                    <td className="px-5 py-3">{r.unit ?? ''}</td>
                    <td className="px-5 py-3 font-mono text-slate-800 dark:text-slate-200">
                      {r.currentValue === null || r.currentValue === undefined ? '' : String(r.currentValue)}
                    </td>
                    <td className="px-5 py-3 font-mono text-slate-600 dark:text-slate-400">{r.currentTimestamp ?? ''}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {checkMutation.data?.rows?.some((r) => r.status !== 'ok') && (
        <div className="mt-4 text-xs text-slate-500 dark:text-slate-400">
          Note: certaines lignes peuvent être vides si aucun attribut Zipato n’existe pour ce type dans le projet, ou si Zipato ne
          renvoie pas de valeur.
        </div>
      )}
    </div>
  );
}

